import {
  ArrowTrendingRegular,
  LeafOneRegular,
  LightbulbRegular,
  WrenchScrewdriverRegular,
} from '@fluentui/react-icons';
import type { MessageDescriptor } from 'react-intl';
import { defineMessage } from 'react-intl';
import { useFeatureFlagEnabled } from '../store';

interface SlugToLevel {
  slug: string;
  levelInt: number;
}

export const Levels: Array<SlugToLevel> = [
  {
    slug: 'one',
    levelInt: 1,
  },
  {
    slug: 'two',
    levelInt: 2,
  },
  {
    slug: 'three',
    levelInt: 3,
  },
  {
    slug: 'four',
    levelInt: 4,
  },
  {
    slug: 'five',
    levelInt: 5,
  },
  {
    slug: 'six',
    levelInt: 6,
  },
  {
    slug: 'seven',
    levelInt: 7,
  },
];

interface LevelName {
  name: MessageDescriptor;
  slug: string;
  levelInt: number;
  icon: typeof LightbulbRegular;
}

export const LevelsOld: Array<LevelName> = [
  {
    name: defineMessage({ defaultMessage: 'Ideate', description: 'Stage name / title for level one accounts' }),
    slug: 'one',
    levelInt: 1,
    icon: LightbulbRegular,
  },
  {
    name: defineMessage({ defaultMessage: 'Develop', description: 'Stage name / title for level two accounts' }),
    slug: 'two',
    levelInt: 2,
    icon: WrenchScrewdriverRegular,
  },
  {
    name: defineMessage({ defaultMessage: 'Grow', description: 'Stage name / title for level three accounts' }),
    slug: 'three',
    levelInt: 3,
    icon: LeafOneRegular,
  },
  {
    name: defineMessage({ defaultMessage: 'Scale', description: 'Stage name / title for level four accounts' }),
    slug: 'four',
    levelInt: 4,
    icon: ArrowTrendingRegular,
  },
];

export const LevelDescriptions: Record<string, MessageDescriptor> = {
  one: defineMessage({
    defaultMessage:
      'You’re refining your idea and learning about the entrepreneurial journey all while exploring how you can build your future product.',
    description: 'Description for accounts at level one',
  }),
  two: defineMessage({
    defaultMessage:
      'You have a strong conviction about the value of your solution and are jumping into the development of your minimum viable product.',
    description: 'Description for accounts at level two',
  }),
  three: defineMessage({
    defaultMessage:
      'You are focused on adding new features to your product as you gain customer traction and market fit.',
    description: 'Description for accounts at level three',
  }),
  four: defineMessage({
    defaultMessage:
      'You have found product market fit with your solution and are ready to focus on sales acceleration.',
    description: 'Description for accounts at level four',
  }),
};

export const getLevelIntFromSlug = (slug: string): number => {
  return Levels.find((lvl) => lvl.slug === slug)!.levelInt;
};

export const accountLevelLabel = defineMessage({
  defaultMessage: 'Level {levelNumber, number}',
  description: 'Account level, 1-4',
});

export const accountLevelToNumber: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
};

export const accountLevelToAzureBenefitSlug: Record<string, string> = {
  one: 'azure-1000',
  two: 'azure-5000',
  three: 'azure-25000',
  four: 'azure-150000',
};

export const slugToValue: Record<string, number> = {
  'azure-1000': 1000,
  'azure-5000': 5000,
  'azure-25000': 25000,
  'azure-50000': 50000,
  'azure-150000': 150000,
  'azure-250000': 250000,
  'azure-350000': 350000,
};

export const accountLevelToAzureBenefitSlugPhase2: Record<string, string> = {
  one: 'azure-1000',
  two: 'azure-5000',
  three: 'azure-25000',
  four: 'azure-50000',
  five: 'azure-150000',
  six: 'azure-250000',
  seven: 'azure-350000',
};

export const useAccountLevelToAzureBenefitSlug = (accountLevel: string): string => {
  const phase2Enabled = useFeatureFlagEnabled('phase2_enabled');

  if (phase2Enabled) {
    return accountLevelToAzureBenefitSlugPhase2[accountLevel];
  }

  return accountLevelToAzureBenefitSlug[accountLevel];
};
